import React, { useMemo, useState } from 'react';
import { PlusIcon, Trash2Icon, PencilIcon, Search, Building2 } from 'lucide-react';
import { Badge, Checkbox, Input, Select, Tag, Spin } from 'antd';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
  ButtonWizard,
  ConfirmationDialog,
  HeadingWizard,
} from '../..';
import { useNotification } from '../../../contexts';
import { CardWizard, SideSheet, SideSheetHeader } from '../../../layouts';
import {
  useCreateCompanyGroupModuleMappingMutation,
  useDeleteCompanyGroupModuleMappingMutation,
  useBulkDeleteCompanyGroupModuleMappingMutation,
  useGetCompanyGroupModuleMappingsQuery,
  useGetCompanyGroupQuery,
  useGetModulesQuery,
} from '../../../store/rtkQuery/master';
import { extractErrorMessageFromAPI } from '../../../utils';
import CommonTravelTable from './CommonTravelTable';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface IModuleMapping {
  mappingId: number;
  moduleId: number;
  moduleName: string;
  moduleDescription?: string;
  code?: string;
  status: number;
}

interface ICompanyGroupModuleMappingRow {
  companyGroupId: number;
  companyGroupName: string;
  clientCode?: string;
  modules: IModuleMapping[];
  status: number;
}

const columnHelper = createColumnHelper<ICompanyGroupModuleMappingRow>();

// ─── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Safely extracts an array from the RTK Query response.
 * Backend shape: { message, data: { list: [...], grouped: [...], count } }
 * RTK Query wraps this as `data` on the hook result.
 */
function safeArray(raw: any, key: string): any[] {
  if (!raw) return [];
  // Direct key on response
  if (Array.isArray(raw?.[key])) return raw[key];
  // Nested under .data (RTK wrapping)
  if (Array.isArray(raw?.data?.[key])) return raw.data[key];
  // Double nested .data.data
  if (Array.isArray(raw?.data?.data?.[key])) return raw.data.data[key];
  // If raw.data is directly an array (some endpoints)
  if (key === 'list' && Array.isArray(raw?.data)) return raw.data;
  return [];
}

// ─── Component ──────────────────────────────────────────────────────────────────

const CompanyGroupModuleMapping: React.FC = () => {
  const { openNotification, showNotification } = useNotification();

  const notify = (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => {
    if (showNotification) {
      showNotification(type, title, message);
    } else if (openNotification) {
      openNotification({ type, title, message });
    }
  };

  // ─── Filter State ───────────────────────────────────────────────────────────
  const [appliedFilters, setAppliedFilters] = useState<{
    page: number;
    pageSize: number;
    search: string;
    companyGroupId?: number | null;
    moduleId?: number | null;
  }>({
    page: 1,
    pageSize: 25,
    search: '',
    companyGroupId: null,
    moduleId: null,
  });

  // ─── Selection state for Bulk Delete ────────────────────────────────────────
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);

  // ─── Drawer / Modal states ──────────────────────────────────────────────────
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const [editingCompanyGroupId, setEditingCompanyGroupId] = useState<number | null>(null);
  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | null>(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState<number[]>([]);
  const [deleteTargetRow, setDeleteTargetRow] = useState<ICompanyGroupModuleMappingRow | null>(null);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);

  // ─── RTK Queries ────────────────────────────────────────────────────────────
  const { data: mappingRes, isLoading, isFetching } = useGetCompanyGroupModuleMappingsQuery({
    page: appliedFilters.page,
    pageSize: appliedFilters.pageSize,
    search: appliedFilters.search || undefined,
    companyGroupId: appliedFilters.companyGroupId || undefined,
    moduleId: appliedFilters.moduleId || undefined,
  });

  const { data: companyGroupRes, isLoading: isCompanyGroupsLoading } = useGetCompanyGroupQuery();
  const { data: modulesRes, isLoading: isModulesLoading } = useGetModulesQuery();

  // ─── RTK Mutations ──────────────────────────────────────────────────────────
  // POST /business-mapping/company-group-module
  // Backend create() does full upsert: soft-deletes removed + upserts remaining
  const [createMapping, { isLoading: isSaving }] = useCreateCompanyGroupModuleMappingMutation();

  // DELETE /business-mapping/company-group-module/:id (single mapping row)
  const [deleteMapping, { isLoading: isDeleting }] = useDeleteCompanyGroupModuleMappingMutation();

  // POST /business-mapping/company-group-module/bulk-delete { ids: [...] }
  const [bulkDeleteMapping, { isLoading: isBulkDeleting }] = useBulkDeleteCompanyGroupModuleMappingMutation();

  // ─── Dropdown Options ───────────────────────────────────────────────────────
  const companyGroupOptions = useMemo(() => {
    // GET /master/company-group → { message, data: { list: [...], count } }
    const list = safeArray(companyGroupRes, 'list');
    return list.map((item: any) => ({
      label: `${item.name || item.groupName}${item.clientCode ? ` (${item.clientCode})` : ''}`,
      value: Number(item.id),
    }));
  }, [companyGroupRes]);

  const moduleOptions = useMemo(() => {
    // GET /master/system-module/all → { message, data: { list: [...], count } }
    const list = safeArray(modulesRes, 'list');
    return list.map((item: any) => ({
      label: item.moduleName || item.name,
      code: item.erpRequestShortCode || '',
      value: Number(item.id),
      description: item.moduleDescription || 'System module',
    }));
  }, [modulesRes]);

  // ─── Table Data ─────────────────────────────────────────────────────────────
  // GET /business-mapping/company-group-module → { message, data: { list, grouped, count } }
  const tableData: ICompanyGroupModuleMappingRow[] = useMemo(() => {
    if (!mappingRes) return [];

    // 1. Try pre-built grouped array from backend
    const grouped = safeArray(mappingRes, 'grouped');
    if (grouped.length > 0) return grouped;

    // 2. Fallback: build grouped view from flat list
    const flatList = safeArray(mappingRes, 'list');
    if (flatList.length > 0) {
      const groupMap = new Map<number, ICompanyGroupModuleMappingRow>();
      for (const item of flatList) {
        const cGroupId = item.companyGroupId || item.companyGroup?.id;
        if (!cGroupId) continue;

        if (!groupMap.has(cGroupId)) {
          groupMap.set(cGroupId, {
            companyGroupId: cGroupId,
            companyGroupName: item.companyGroup?.name || item.companyGroupName || `Group #${cGroupId}`,
            clientCode: item.companyGroup?.clientCode || item.clientCode || '',
            modules: [],
            status: item.status ?? 1,
          });
        }
        const record = groupMap.get(cGroupId)!;
        const mod = item.module || {};
        record.modules.push({
          mappingId: item.id,
          moduleId: item.moduleId || mod.id,
          moduleName: mod.moduleName || item.moduleName || 'Module',
          moduleDescription: mod.moduleDescription || '',
          code: mod.erpRequestShortCode || item.code || '',
          status: item.status ?? 1,
        });
      }
      return Array.from(groupMap.values());
    }

    return [];
  }, [mappingRes]);

  const totalItems = tableData.length;
  const totalMappedModulesCount = useMemo(
    () => tableData.reduce((acc, row) => acc + (row.modules?.length || 0), 0),
    [tableData],
  );

  // ─── Selection helpers ──────────────────────────────────────────────────────
  const selectedMappingIds = useMemo(() => {
    const ids: number[] = [];
    tableData.forEach(row => {
      if (selectedGroupIds.includes(row.companyGroupId)) {
        row.modules.forEach(m => ids.push(m.mappingId));
      }
    });
    return ids;
  }, [selectedGroupIds, tableData]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  /** Open drawer for creating a new mapping */
  const handleOpenCreate = () => {
    setEditingCompanyGroupId(null);
    setSelectedCompanyGroupId(companyGroupOptions[0]?.value || null);
    setSelectedModuleIds([]); // Start with none selected
    setIsSideSheetOpen(true);
  };

  /** Open drawer for editing an existing company group's module mappings */
  const handleOpenEdit = (row: ICompanyGroupModuleMappingRow) => {
    setEditingCompanyGroupId(row.companyGroupId);
    setSelectedCompanyGroupId(row.companyGroupId);
    setSelectedModuleIds(row.modules.map(m => Number(m.moduleId)));
    setIsSideSheetOpen(true);
  };

  /**
   * Save handler for both Create and Edit.
   * Backend POST /business-mapping/company-group-module does full upsert:
   * - Soft-deletes any existing mappings NOT in moduleIds
   * - Upserts (create or reactivate) mappings IN moduleIds
   * Tag invalidation on 'CompanyGroupModuleMapping' auto-refetches the list.
   */
  const handleSave = async () => {
    if (!selectedCompanyGroupId) {
      notify('warning', 'Validation', 'Please select a Company Group');
      return;
    }
    if (selectedModuleIds.length === 0) {
      notify('warning', 'Validation', 'Please select at least one module');
      return;
    }

    try {
      await createMapping({
        companyGroupId: selectedCompanyGroupId,
        moduleIds: selectedModuleIds,
      }).unwrap();

      notify(
        'success',
        'Success',
        editingCompanyGroupId ? 'Mapping updated successfully' : 'Mapping created successfully',
      );
      setIsSideSheetOpen(false);
      setEditingCompanyGroupId(null);
    } catch (err: any) {
      notify('error', 'Error', extractErrorMessageFromAPI(err, 'Failed to save mapping'));
    }
  };

  /**
   * Delete all module mappings for a single company group row.
   * Uses bulk-delete with all mapping IDs from that row.
   */
  const handleDeleteSingleConfirm = async () => {
    if (!deleteTargetRow) return;
    const mappingIds = deleteTargetRow.modules.map(m => m.mappingId);
    if (mappingIds.length === 0) return;

    try {
      if (mappingIds.length === 1) {
        // Single mapping: use DELETE endpoint
        await deleteMapping(mappingIds[0]).unwrap();
      } else {
        // Multiple mappings: use bulk-delete endpoint
        await bulkDeleteMapping(mappingIds).unwrap();
      }
      notify('success', 'Success', `All mappings for "${deleteTargetRow.companyGroupName}" deleted`);
      setDeleteTargetRow(null);
    } catch (err: any) {
      notify('error', 'Error', extractErrorMessageFromAPI(err, 'Failed to delete mapping'));
    }
  };

  /**
   * Bulk delete: deletes all module mappings for all selected company group rows.
   */
  const handleBulkDeleteConfirm = async () => {
    if (selectedMappingIds.length === 0) return;
    try {
      await bulkDeleteMapping(selectedMappingIds).unwrap();
      notify('success', 'Success', `Deleted ${selectedMappingIds.length} mappings`);
      setSelectedGroupIds([]);
      setIsBulkDeleteOpen(false);
    } catch (err: any) {
      notify('error', 'Error', extractErrorMessageFromAPI(err, 'Failed to bulk delete'));
    }
  };

  // ─── Table Columns ──────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        size: 40,
        header: () => (
          <Checkbox
            checked={tableData.length > 0 && selectedGroupIds.length === tableData.length}
            indeterminate={selectedGroupIds.length > 0 && selectedGroupIds.length < tableData.length}
            onChange={e => {
              if (e.target.checked) {
                setSelectedGroupIds(tableData.map(r => r.companyGroupId));
              } else {
                setSelectedGroupIds([]);
              }
            }}
          />
        ),
        cell: info => {
          const groupId = info.row.original.companyGroupId;
          return (
            <Checkbox
              checked={selectedGroupIds.includes(groupId)}
              onChange={e => {
                if (e.target.checked) {
                  setSelectedGroupIds(prev => [...prev, groupId]);
                } else {
                  setSelectedGroupIds(prev => prev.filter(id => id !== groupId));
                }
              }}
            />
          );
        },
      }),
      columnHelper.display({
        id: 'index',
        size: 50,
        header: '#',
        cell: info => (appliedFilters.page - 1) * appliedFilters.pageSize + info.row.index + 1,
      }),
      columnHelper.accessor('companyGroupName', {
        id: 'companyGroupName',
        size: 240,
        header: 'Company Group',
        cell: info => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs shrink-0">
                <Building2 className="w-4 h-4" />
              </div>
              <div className="truncate">
                <span className="font-semibold text-gray-900 block truncate">{row.companyGroupName}</span>
                {row.clientCode && <span className="text-[11px] text-gray-500 block">Code: {row.clientCode}</span>}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('modules', {
        id: 'modules',
        size: 320,
        header: 'Mapped Modules',
        cell: info => {
          const modules = info.getValue() || [];
          if (modules.length === 0) {
            return <span className="text-xs text-gray-400 italic">No modules mapped</span>;
          }
          return (
            <div className="flex flex-wrap gap-1.5 max-w-md">
              {modules.map((m, idx) => (
                <Tag key={idx} color="blue" className="text-xs px-2 py-0.5 rounded-md font-medium">
                  {m.moduleName}
                </Tag>
              ))}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'shortCodes',
        size: 130,
        header: 'Short Codes',
        cell: info => {
          const modules = info.row.original.modules || [];
          const codes = modules.map(m => m.code).filter(Boolean);
          if (codes.length === 0) return <span className="text-xs text-gray-400">-</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {codes.map((c, i) => (
                <Badge key={i} count={c} style={{ backgroundColor: '#4f46e5', fontSize: '10px' }} />
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        size: 100,
        header: 'Status',
        cell: info => {
          const status = info.getValue();
          return status === 1 ? (
            <Tag color="success" className="rounded-full px-2.5 text-xs font-semibold">
              Active
            </Tag>
          ) : (
            <Tag color="default" className="rounded-full px-2.5 text-xs font-semibold">
              Inactive
            </Tag>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        size: 100,
        header: 'Actions',
        cell: info => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleOpenEdit(row)}
                className="p-1 hover:bg-slate-100 rounded text-indigo-600 transition-colors"
                title="Edit Mapping"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setDeleteTargetRow(row)}
                className="p-1 hover:bg-rose-50 rounded text-rose-600 transition-colors"
                title="Delete All Mappings"
              >
                <Trash2Icon className="w-4 h-4" />
              </button>
            </div>
          );
        },
      }),
    ],
    [appliedFilters.page, appliedFilters.pageSize, selectedGroupIds, tableData],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-1.5 flex flex-col gap-2">
      <Spin spinning={isLoading || isFetching || isSaving || isDeleting || isBulkDeleting}>
        <CardWizard addOnClass="p-0 pt-2.5">
          <div>
            {/* Header */}
            <div className="flex items-center justify-between px-4 mb-3">
              <HeadingWizard addOnClass="text-[#6E6893] font-extrabold uppercase tracking-wider text-sm flex items-center gap-x-2">
                <span>Company Group</span>
                <span>&</span>
                <span>Module Mapping List</span>
                {totalMappedModulesCount > 0 && (
                  <Tag color="purple" className="ml-2 font-bold text-xs rounded-full px-2.5 py-0.5 border-none normal-case">
                    {tableData.length} Groups · {totalMappedModulesCount} Mappings
                  </Tag>
                )}
              </HeadingWizard>

              <div className="flex items-center justify-end gap-2">
                {selectedGroupIds.length > 0 && (
                  <ButtonWizard
                    addOnClass="border border-error-500 mr-auto"
                    addOnTextClass="text-error-500"
                    buttonText={`Delete Selected (${selectedGroupIds.length})`}
                    onBtnClick={() => setIsBulkDeleteOpen(true)}
                  >
                    <Trash2Icon className="size-4 stroke-[3px] text-error-500 w-4 h-4" />
                  </ButtonWizard>
                )}

                <ButtonWizard
                  addOnClass="bg-primary-600 h-8 px-4 flex items-center justify-center"
                  buttonText="Add Mapping"
                  onBtnClick={handleOpenCreate}
                >
                  <PlusIcon className="w-4 h-4 text-white" />
                </ButtonWizard>
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-[#F2F0F9] p-3 mx-4 mb-3 rounded-lg border border-slate-200">
              <div>
                <label className="block text-[11px] font-bold text-[#6E6893] uppercase tracking-wider mb-1">
                  Search
                </label>
                <Input
                  placeholder="Search Company Group or Module..."
                  prefix={<Search className="w-4 h-4 text-slate-400" />}
                  value={appliedFilters.search}
                  onChange={e =>
                    setAppliedFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))
                  }
                  allowClear
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-[#6E6893] uppercase tracking-wider mb-1">
                  Filter Company Group
                </label>
                <Select
                  className="w-full"
                  placeholder="All Company Groups"
                  loading={isCompanyGroupsLoading}
                  options={companyGroupOptions}
                  value={appliedFilters.companyGroupId}
                  onChange={val =>
                    setAppliedFilters(prev => ({ ...prev, companyGroupId: val, page: 1 }))
                  }
                  allowClear
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-[#6E6893] uppercase tracking-wider mb-1">
                  Filter Module
                </label>
                <Select
                  className="w-full"
                  placeholder="All Modules"
                  loading={isModulesLoading}
                  options={moduleOptions}
                  value={appliedFilters.moduleId}
                  onChange={val =>
                    setAppliedFilters(prev => ({ ...prev, moduleId: val, page: 1 }))
                  }
                  allowClear
                />
              </div>
            </div>

            {/* Table */}
            <CommonTravelTable
              key={`mapping-table-${tableData.length}-${isFetching}`}
              table={table}
              total={totalItems}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
            />
          </div>
        </CardWizard>
      </Spin>

      {/* ─── SideSheet: Add / Edit ─────────────────────────────────────────── */}
      {isSideSheetOpen && (
        <SideSheet
          isOpen={isSideSheetOpen}
          onClose={() => setIsSideSheetOpen(false)}
          headerChildren={
            <SideSheetHeader
              title={editingCompanyGroupId ? 'Edit Company Group Module Mapping' : 'Add Company Group Module Mapping'}
              onClose={() => setIsSideSheetOpen(false)}
            />
          }
          contentChildren={
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Select Company Group <span className="text-rose-500">*</span>
                </label>
                <Select
                  className="w-full"
                  placeholder="Select Company Group..."
                  loading={isCompanyGroupsLoading}
                  options={companyGroupOptions}
                  value={selectedCompanyGroupId}
                  disabled={!!editingCompanyGroupId}
                  onChange={val => setSelectedCompanyGroupId(val)}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Select Accessible System Modules
                </label>
                {isModulesLoading ? (
                  <div className="py-8 text-center bg-slate-50 rounded-xl border border-slate-200">
                    <Spin size="small" />
                    <p className="text-xs text-slate-500 mt-2">Loading system modules...</p>
                  </div>
                ) : moduleOptions.length === 0 ? (
                  <div className="py-8 text-center bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-xs text-slate-500">No system modules found in the database.</p>
                  </div>
                ) : (
                  <div className="space-y-2.5 bg-slate-50 p-3.5 rounded-xl border border-slate-200 max-h-72 overflow-y-auto">
                    {moduleOptions.map(mod => {
                      const isChecked = selectedModuleIds.includes(mod.value);
                      return (
                        <div
                          key={mod.value}
                          onClick={() => {
                            if (isChecked) {
                              setSelectedModuleIds(prev => prev.filter(id => id !== mod.value));
                            } else {
                              setSelectedModuleIds(prev => [...prev, mod.value]);
                            }
                          }}
                          className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all border ${
                            isChecked
                              ? 'bg-indigo-50/80 border-indigo-200 shadow-sm'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            checked={isChecked}
                            onChange={() => {}}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-xs text-slate-900">{mod.label}</span>
                              {mod.code && (
                                <span className="bg-indigo-100 text-indigo-700 font-bold text-[10px] px-2 py-0.5 rounded">
                                  {mod.code}
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] text-slate-500 block mt-0.5">{mod.description}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          }
          footerChildren={
            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-white">
              <ButtonWizard
                addOnClass="bg-white border border-primary-600 px-4 h-9 flex items-center justify-center rounded-lg hover:bg-slate-50 transition-colors"
                addOnTextClass="text-primary-600 font-semibold text-xs"
                buttonText="Cancel"
                onBtnClick={() => setIsSideSheetOpen(false)}
              />
              <ButtonWizard
                addOnClass="bg-primary-600 px-5 h-9 flex items-center justify-center rounded-lg hover:bg-primary-700 transition-colors"
                addOnTextClass="text-white font-semibold text-xs"
                buttonText={editingCompanyGroupId ? 'Update Mapping' : 'Save Mapping'}
                onBtnClick={handleSave}
              />
            </div>
          }
        />
      )}

      {/* ─── Delete Confirmation: Single Row ───────────────────────────────── */}
      <ConfirmationDialog
        isOpen={!!deleteTargetRow}
        title="Delete Module Mapping"
        description={
          deleteTargetRow
            ? `Are you sure you want to remove all ${deleteTargetRow.modules.length} module mapping(s) for "${deleteTargetRow.companyGroupName}"?`
            : ''
        }
        confirmText="Delete"
        confirmButtonVariant="danger"
        isLoading={isDeleting || isBulkDeleting}
        onConfirm={handleDeleteSingleConfirm}
        onClose={() => setDeleteTargetRow(null)}
      />

      {/* ─── Delete Confirmation: Bulk ─────────────────────────────────────── */}
      <ConfirmationDialog
        isOpen={isBulkDeleteOpen}
        title={`Bulk Delete ${selectedGroupIds.length} Company Group(s)`}
        description={`This will delete ${selectedMappingIds.length} module mapping(s) across ${selectedGroupIds.length} company group(s). This action cannot be undone.`}
        confirmText={`Delete ${selectedMappingIds.length} Mappings`}
        confirmButtonVariant="danger"
        isLoading={isBulkDeleting}
        onConfirm={handleBulkDeleteConfirm}
        onClose={() => setIsBulkDeleteOpen(false)}
      />
    </div>
  );
};

export default CompanyGroupModuleMapping;
